import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { isGcalMultiCalendarEnabled } from "@/lib/gcal-feature-flags";

const OPEN_SYNC_MIN_INTERVAL_MS = 4 * 60 * 60 * 1000;

interface UseGoogleCalendarAutoSyncArgs {
  workspaceId: string | null;
  hasGoogleCalendar: boolean;
  needsReconnect?: boolean;
  onComplete?: () => void;
}

type SyncTarget = {
  calendarId: string;
  autoSyncOnOpen: boolean;
  lastSyncedAt: number;
};

async function loadSyncTargets(workspaceId: string): Promise<SyncTarget[]> {
  if (isGcalMultiCalendarEnabled()) {
    const [{ data: calendars }, { data: syncStates }] = await Promise.all([
      supabase
        .from("google_calendar_sync_calendars")
        .select("calendar_id, auto_sync_on_open")
        .eq("workspace_id", workspaceId)
        .eq("is_enabled", true),
      supabase
        .from("google_calendar_sync_state")
        .select("calendar_id, last_synced_at")
        .eq("workspace_id", workspaceId),
    ]);

    const stateByCalendar = new Map(
      (syncStates ?? []).map((s) => [s.calendar_id, s.last_synced_at]),
    );

    return (calendars ?? []).map((cal) => ({
      calendarId: cal.calendar_id,
      autoSyncOnOpen: cal.auto_sync_on_open !== false,
      lastSyncedAt: stateByCalendar.get(cal.calendar_id)
        ? new Date(stateByCalendar.get(cal.calendar_id)!).getTime()
        : 0,
    }));
  }

  const { data: credentials } = await supabase
    .from("google_calendar_credentials")
    .select("auto_sync_on_open, calendar_id")
    .eq("workspace_id", workspaceId)
    .eq("is_active", true)
    .maybeSingle();

  if (!credentials) return [];

  const calendarId = credentials.calendar_id || "primary";
  const { data: syncState } = await supabase
    .from("google_calendar_sync_state")
    .select("last_synced_at")
    .eq("workspace_id", workspaceId)
    .eq("calendar_id", calendarId)
    .maybeSingle();

  return [{
    calendarId,
    autoSyncOnOpen: credentials.auto_sync_on_open !== false,
    lastSyncedAt: syncState?.last_synced_at
      ? new Date(syncState.last_synced_at).getTime()
      : 0,
  }];
}

export function useGoogleCalendarAutoSync({
  workspaceId,
  hasGoogleCalendar,
  needsReconnect = false,
  onComplete,
}: UseGoogleCalendarAutoSyncArgs) {
  const [syncing, setSyncing] = useState(false);
  const attemptedRef = useRef<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!workspaceId || !hasGoogleCalendar || needsReconnect) return;
    const attemptKey = `${workspaceId}:${hasGoogleCalendar}:${needsReconnect}`;
    if (attemptedRef.current === attemptKey) return;
    attemptedRef.current = attemptKey;

    let cancelled = false;

    const run = async () => {
      try {
        const targets = await loadSyncTargets(workspaceId);
        const dueTargets = targets.filter((t) => {
          if (!t.autoSyncOnOpen) return false;
          if (!t.lastSyncedAt) return true;
          return Date.now() - t.lastSyncedAt >= OPEN_SYNC_MIN_INTERVAL_MS;
        });

        if (!dueTargets.length) return;
        if (cancelled) return;
        setSyncing(true);

        let anySucceeded = false;

        for (const target of dueTargets) {
          const { data, error } = await supabase.functions.invoke("google-calendar-sync", {
            body: {
              action: "sync_from_google",
              workspaceId,
              reason: "open",
              calendarId: target.calendarId,
            },
          });

          if (cancelled) return;
          if (error) throw error;

          if (data?.error === "GOOGLE_AUTH_NEEDS_RECONNECT") {
            toast({
              title: "Reconecte o Google Calendar",
              description: "A conexão precisa ser refeita para sincronizar agendamentos.",
              variant: "destructive",
            });
            return;
          }

          if (data?.error === "SYNC_ALREADY_RUNNING") continue;
          anySucceeded = true;
        }

        if (anySucceeded) onComplete?.();
      } catch (err) {
        console.warn("Google Calendar auto sync skipped:", err);
      } finally {
        if (!cancelled) setSyncing(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, hasGoogleCalendar, needsReconnect, onComplete, toast]);

  return { syncing };
}
